import React, { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import EventSessionForm from "../EventSessionForm/EventSessionForm";
import EventSessionFormLoadingSkeleton from "../EventSessionFormLoadingSkeleton/EventSessionFormLoadingSkeleton";
import { fetchSingleEventSessionAction } from "../../slices/createUpdateEventSessionSlice";
import { useEventSessionForm } from "../../hooks/useEventSessionForm";

interface EditEventSessionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  sessionUuid: string | null;
  onSuccess?: () => void;
  eventUuid: string;
}

function EditEventSessionDrawer({
  isOpen,
  onClose,
  sessionUuid,
  eventUuid,
  onSuccess,
}: EditEventSessionDrawerProps) {
  const dispatch = useAppDispatch();

  const { loadingFetchSingle, fetchSingleError, singleSession } =
    useAppSelector((state: RootState) => state.createUpdateEventSession);

  useEffect(() => {
    if (isOpen && sessionUuid) {
      dispatch(fetchSingleEventSessionAction(sessionUuid));
    }
  }, [isOpen, sessionUuid, dispatch]);

  const { handleClose: clearFormSessionDays } = useEventSessionForm({
    isEditMode: true,
    eventUuid,
    sessionUuid,
  });

  const handleClose = () => {
    clearFormSessionDays();
    onClose();
  };

  return (
    <Transition.Root as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-40" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    <div className="flex items-center justify-between bg-primary px-4 py-4">
                      <Dialog.Title className="text-lg font-medium text-white">
                        Edit Event Session
                      </Dialog.Title>
                      <button
                        className="text-white"
                        onClick={handleClose}
                        type="button"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                      {fetchSingleError && (
                        <p className="mb-2 text-sm text-red-500">
                          {fetchSingleError}
                        </p>
                      )}

                      {loadingFetchSingle ? (
                        <EventSessionFormLoadingSkeleton />
                      ) : singleSession ? (
                        <EventSessionForm
                          eventUuid={eventUuid}
                          isEditMode={true}
                          isFinalized={singleSession.isFinalized}
                          onSuccessCallback={onSuccess}
                          sessionUuid={sessionUuid}
                        />
                      ) : (
                        <p className="text-sm text-gray-500">
                          No session found or an error occurred.
                        </p>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default EditEventSessionDrawer;
